import React from 'react';
import { Box, Container, Grid, Stack, Typography } from '@mui/material';
import { alpha, darken, useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { AUDIENCES, AUDIENCE_HEADING, PROOF_POINTS } from '../demoContent';
import IconTile from './IconTile';
import SectionHeading from './SectionHeading';

/**
 * Two bands: what the platform actually guarantees (navy), and who it is built for (white).
 * Every proof point maps to shipped behaviour — no invented numbers, no implied customer list.
 */
export default function ProofSection() {
  const theme = useTheme();

  return (
    <>
      <Box
        component="section"
        sx={{
          color: 'common.white',
          background: `linear-gradient(135deg, ${darken(theme.palette.primary.dark, 0.3)} 0%, ${
            theme.palette.primary.dark
          } 100%)`,
          py: { xs: 7, md: 9 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 5 }}>
            {PROOF_POINTS.map((point, index) => (
              <Grid item xs={12} sm={6} md={3} key={point.title}>
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.5, delay: index * 0.07, ease: 'easeOut' }}
                >
                  <IconTile Icon={point.Icon} onDark />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, color: 'common.white' }}>
                    {point.title}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: alpha('#FFFFFF', 0.75), lineHeight: 1.65 }}>
                    {point.description}
                  </Typography>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ bgcolor: 'background.paper', py: { xs: 8, md: 11 } }}>
        <Container maxWidth="lg">
          <SectionHeading
            overline={AUDIENCE_HEADING.overline}
            heading={AUDIENCE_HEADING.heading}
          />

          <Grid container spacing={{ xs: 4, md: 5 }}>
            {AUDIENCES.map((audience, index) => (
              <Grid item xs={12} sm={6} md={3} key={audience.title}>
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.5, delay: index * 0.07, ease: 'easeOut' }}
                >
                  <Stack direction="row" spacing={2}>
                    <IconTile Icon={audience.Icon} accent={audience.accent} size={44} />
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {audience.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, lineHeight: 1.6 }}>
                        {audience.description}
                      </Typography>
                    </Box>
                  </Stack>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </>
  );
}
